import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  capitalize,
} from '@mui/material'
import {
  CancelRounded,
  CheckCircleRounded,
  OpenInNewRounded,
} from '@mui/icons-material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import DateTimeDisplay from '../DateTimeDisplay'
import RelationField from './RelationField'
import dayjs from 'dayjs'
import { resolveNavPath } from '../../utilities/pathMapping'
import { useEffect } from 'react'
import { useUser } from '../../context/CurrentUserContext'

const DEFAULT_RESPONSIBLE_ROLES = ['admin']

export const ISSUE_TYPES = ['trivial', 'minor', 'moderate', 'major', 'critical']

export const ISSUE_SEVERITY_TO_ALERT = {
  trivial: 'info',
  minor: 'info',
  moderate: 'warning',
  major: 'error',
  critical: 'error',
}

// Maps field types to their HTML input type attribute for TextField
export const TEXT_FIELD_INPUT_TYPES = {
  text: 'text',
  number: 'number',
  email: 'email',
  url: 'url',
  ip: 'text',
  semver: 'text',
  googleDriveLink: 'url',
  time: 'time',
}

export const COLOR_CODED_VALUES = {
  retired: 'error',
  push: 'warning',
  pull: 'info',
  off: 'neutral',
  on: 'success',
  test: 'info',
}

const LinkRenderer = ({ value, label, external }) =>
  value && (
    <Stack direction={'row'} spacing={1} justifyContent={'center'}>
      <Link
        href={value}
        target={external && '_blank'}
        rel="noopener noreferrer"
        variant="body1"
        noWrap
      >
        {label}
      </Link>
      {external && (
        <OpenInNewRounded fontSize="small" color="primary" aria-hidden />
      )}
    </Stack>
  )

const ChipListRenderer = ({ value, options }) => (
  <Stack gap={1} direction="row" flexWrap="wrap">
    {Array.isArray(value)
      ? value.map((v) => (
          <Chip
            key={v}
            label={options?.find((o) => o.key === v)?.label ?? v}
            size="small"
            color={COLOR_CODED_VALUES[v] ?? 'default'}
          />
        ))
      : null}
  </Stack>
)

export const FIELD_VIEW_RENDERERS = {
  boolean: ({ value }) =>
    value ? (
      <CheckCircleRounded color="success" aria-label="Yes" />
    ) : (
      <CancelRounded color="neutral" aria-label="No" />
    ),

  date: ({ value }) => (
    <Typography variant="body1" color="text.primary" noWrap>
      {value && <DateTimeDisplay date={value} showTime={false} />}
    </Typography>
  ),

  datetime: ({ value }) => (
    <Typography variant="body1" color="text.primary" noWrap>
      {value && <DateTimeDisplay date={value} showTime={true} />}
    </Typography>
  ),

  url: LinkRenderer,
  googleDriveLink: LinkRenderer,

  select: ({ value, options }) => {
    const option = options?.find((o) => o.key === value)
    return (
      <Chip
        label={option?.label ?? value ?? 'No data'}
        size="small"
        color={COLOR_CODED_VALUES[value] ?? 'default'}
      />
    )
  },

  multiselect: ChipListRenderer,
  tag: ChipListRenderer,

  dependent: ({ value }) =>
    value ? (
      <Chip
        label={value?.label ?? value?.key}
        size="small"
        color={COLOR_CODED_VALUES[value?.key] ?? 'default'}
      />
    ) : null,

  size: ({ value }) => {
    const display = value?.value != null && `${value.value} ${value.unit}`
    return (
      <Typography variant="body1" color="text.primary" noWrap>
        {display}
      </Typography>
    )
  },

  relation: ({ value, path }) => {
    const url =
      path && value?.uuid ? `/${resolveNavPath(path)}/${value.uuid}` : null
    return url ? (
      <Link href={url} variant="body1" noWrap>
        {value.name ?? value.hardware_identifier}
      </Link>
    ) : (
      <Typography variant="body1" color="text.primary" noWrap>
        {value?.name ?? value?.hardware_identifier}
      </Typography>
    )
  },

  relations: ({ value, path }) => {
    const items = Array.isArray(value) ? value : []
    return (
      items.length !== 0 && (
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          justifyContent="flex-end"
        >
          {items.map((r, i) =>
            path && r.uuid ? (
              <Link
                key={r.uuid}
                href={`/${resolveNavPath(path)}/${r.uuid}`}
                variant="body1"
              >
                {r.name}
              </Link>
            ) : (
              <Typography key={i} variant="body1" color="text.primary">
                {r.name}
              </Typography>
            ),
          )}
        </Stack>
      )
    )
  },

  issue: ({ value }) => (
    <Alert
      severity={
        ISSUE_SEVERITY_TO_ALERT[value?.type ?? value?.severity] ?? 'success'
      }
    >
      <AlertTitle>
        {value?.type ? capitalize(value?.type) : 'No'} issue(s)
      </AlertTitle>
      {value?.description}
    </Alert>
  ),

  content: ({ label, variant }) => (
    <Typography variant={variant} sx={{ whiteSpace: 'pre-line' }}>
      {label}
    </Typography>
  ),

  default: ({ value }) => (
    <Typography variant="body1" color="text.primary" noWrap>
      {value != null && typeof value !== 'object' && value}
    </Typography>
  ),
}

const validateField = (
  type,
  value,
  { minLength, maxLength, min, max } = {},
) => {
  if (!value && value !== 0) return null
  switch (type) {
    case 'text':
    case 'textarea':
      if (minLength != null && value.length < minLength)
        return `Minimum ${minLength} characters`
      if (maxLength != null && value.length > maxLength)
        return `Maximum ${maxLength} characters`
      return null
    case 'number': {
      const num = parseFloat(value)
      if (min != null && num < min) return `Minimum value is ${min}`
      if (max != null && num > max) return `Maximum value is ${max}`
      return null
    }
    case 'email':
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
        ? null
        : 'Invalid email address'
    case 'url':
      return value.startsWith('https://') ? null : 'URL must use HTTPS'
    case 'googleDriveLink':
      return value.startsWith('https://drive.google.com/') ||
        value.startsWith('https://docs.google.com/')
        ? null
        : 'Must be a valid drive.google.com or docs.google.com link'
    case 'ip':
      return /^(\d{1,3}\.){3}\d{1,3}$/.test(value) ||
        /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}$/.test(value)
        ? null
        : 'Must be a valid IPv4 or IPv6 address'
    case 'date': {
      const d = dayjs(value)
      if (min && d.isBefore(dayjs(min)))
        return `Date must be on or after ${min}`
      if (max && d.isAfter(dayjs(max)))
        return `Date must be on or before ${max}`
      return null
    }
    case 'semver':
      return /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/.test(
        value,
      )
        ? null
        : 'Must be a valid semantic version (e.g. 1.0.0, 2.0.0-rc.1)'
    case 'mac':
      return /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/.test(value)
        ? null
        : 'Must have format XX:XX:XX:XX:XX:XX'
    case 'hardwareIdentifier':
      return /^\d{4}-[A-Za-z]{2}\d{2}-[A-Za-z0-9]{4}$/.test(value)
        ? null
        : 'Must have format YYMM-TTxx-ZZZZ, where YY is year, MM is month, TT is model, xx is model number, ZZZZ is serial code.'
    default:
      return null
  }
}

const toFieldId = (label, suffix) =>
  `${label
    ?.toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}-${suffix}`

const resolveFieldError = (
  externalError,
  externalHelperText,
  validationError,
) => ({
  error: externalError || !!validationError,
  helperText: externalError
    ? externalHelperText
    : (validationError ?? externalHelperText),
})

export const FIELD_EDIT_RENDERERS = {
  boolean: ({ label, value, readOnly, onChange }) => (
    <FormControlLabel
      control={
        <Switch
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          disabled={readOnly}
        />
      }
      label={label}
    />
  ),

  textarea: ({
    label,
    value,
    required,
    placeholder,
    rows,
    readOnly,
    onChange,
    error,
    helperText,
    minLength,
    maxLength,
  }) => {
    const inputId = toFieldId(label, 'textarea')
    const { error: fieldError, helperText: fieldHelperText } =
      resolveFieldError(
        error,
        helperText,
        validateField('textarea', value, { minLength, maxLength }),
      )
    return (
      <TextField
        id={inputId}
        slotProps={{
          htmlInput: {
            id: inputId,
          },
        }}
        label={label}
        multiline
        rows={rows ?? 3}
        required={required}
        placeholder={placeholder}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        fullWidth
        disabled={readOnly}
        error={fieldError}
        helperText={fieldHelperText}
      />
    )
  },

  tag: ({
    label,
    value,
    required,
    options,
    readOnly,
    onChange,
    error,
    helperText,
    allowNew,
    max,
    responsibleRoles = DEFAULT_RESPONSIBLE_ROLES,
  }) => {
    const { user } = useUser()
    const hasPermission = responsibleRoles.includes(user?.role)

    useEffect(() => {
      if (options?.length === 1) {
        const key = options[0].key
        const currentValue = Array.isArray(value) ? value : []
        if (!currentValue.includes(key)) onChange([key])
      }
    }, [options, value, onChange])

    return (
      <Autocomplete
        freeSolo={allowNew && hasPermission}
        multiple
        options={options ?? []}
        getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
        isOptionEqualToValue={(option, val) =>
          option.key === (typeof val === 'string' ? val : val.key)
        }
        value={
          Array.isArray(value)
            ? value.map((v) => options?.find((o) => o.key === v) ?? v)
            : []
        }
        onChange={(_, newValue) => {
          if (newValue.length > max) return
          onChange(newValue.map((v) => (typeof v === 'string' ? v : v.key)))
        }}
        disabled={readOnly || (options?.length === 1 && !hasPermission)}
        size="small"
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            error={error}
            helperText={
              max
                ? `Select max. ${max} option(s). ${helperText ? helperText : ''}`
                : helperText
            }
          />
        )}
      />
    )
  },

  select: ({
    label,
    value,
    required,
    options,
    readOnly,
    onChange,
    error,
    helperText,
  }) => {
    useEffect(() => {
      if (options?.length === 1 && value !== options[0].key) {
        onChange(options[0].key)
      }
    }, [options, value, onChange])

    const labelId = toFieldId(label, 'label')
    const inputId = toFieldId(label, 'input')

    return (
      <FormControl size="small" fullWidth required={required} error={error}>
        <InputLabel id={labelId} htmlFor={inputId}>
          {label}
        </InputLabel>
        <Select
          value={value ?? ''}
          label={label}
          labelId={labelId}
          inputProps={{ id: inputId }}
          onChange={(e) => onChange(e.target.value)}
          disabled={readOnly || options?.length === 1}
        >
          {options?.map((opt) => (
            <MenuItem key={opt.key} value={opt.key}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    )
  },

  multiselect: ({
    label,
    value,
    required,
    options,
    readOnly,
    onChange,
    error,
    helperText,
  }) => {
    useEffect(() => {
      const currentValue = Array.isArray(value) ? value : []
      if (options?.length === 1 && !currentValue.includes(options[0].key)) {
        onChange([options[0].key])
      }
    }, [options, value, onChange])

    const labelId = toFieldId(label, 'label')
    const inputId = toFieldId(label, 'input')

    return (
      <FormControl size="small" fullWidth required={required} error={error}>
        <InputLabel id={labelId} htmlFor={inputId}>
          {label}
        </InputLabel>
        <Select
          multiple
          value={Array.isArray(value) ? value : []}
          label={label}
          labelId={labelId}
          inputProps={{ id: inputId }}
          onChange={(e) => onChange(e.target.value)}
          disabled={readOnly || options?.length === 1}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((v) => {
                const opt = options?.find((o) => o.key === v)
                return (
                  <Chip
                    key={v}
                    label={opt?.label ?? v}
                    size="small"
                    color={COLOR_CODED_VALUES[v] ?? 'default'}
                  />
                )
              })}
            </Box>
          )}
        >
          {options?.map((opt) => (
            <MenuItem key={opt.key} value={opt.key}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    )
  },

  dependent: ({ value }) =>
    value ? (
      <Chip
        label={value?.label ?? value?.key}
        size="small"
        color={COLOR_CODED_VALUES[value?.key] ?? 'default'}
      />
    ) : null,

  date: ({
    label,
    value,
    required,
    readOnly,
    onChange,
    error,
    helperText,
    min,
    max,
  }) => {
    const minDate = min ? dayjs(min) : undefined
    const maxDate = max ? dayjs(max) : undefined
    const { error: fieldError, helperText: fieldHelperText } =
      resolveFieldError(
        error,
        helperText,
        validateField('date', value, { min, max }),
      )
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label={label}
          value={value ? dayjs(value) : null}
          onChange={(date) => onChange(date?.toISOString() ?? null)}
          disabled={readOnly}
          minDate={minDate}
          maxDate={maxDate}
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
              required,
              error: fieldError,
              helperText: fieldHelperText,
            },
          }}
        />
      </LocalizationProvider>
    )
  },

  size: ({
    label,
    value,
    defaultUnit,
    units,
    readOnly,
    onChange,
    error,
    helperText,
  }) => (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <TextField
        label={label}
        type="number"
        value={value?.value ?? ''}
        onChange={(e) =>
          onChange({
            ...value,
            value: e.target.value,
            unit: value?.unit ?? defaultUnit,
          })
        }
        size="small"
        disabled={readOnly}
        error={error}
        helperText={helperText}
        sx={{ flex: 1 }}
      />
      <FormControl size="small" sx={{ minWidth: 80 }}>
        <InputLabel id="unit-label" htmlFor="unit-input">
          Unit
        </InputLabel>
        <Select
          value={value?.unit ?? defaultUnit ?? 'GB'}
          label="Unit"
          labelId="unit-label"
          inputProps={{ id: 'unit-input' }}
          onChange={(e) => onChange({ ...value, unit: e.target.value })}
          disabled={readOnly}
        >
          {units.map((u) => (
            <MenuItem key={u} value={u}>
              {u}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  ),

  issue: ({ label, value, onChange }) => (
    <Stack spacing={1}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <FormControl size="small" fullWidth>
        <InputLabel id="severity-label" htmlFor="severity-input">
          Severity
        </InputLabel>
        <Select
          value={value?.severity ?? ''}
          label="Severity"
          labelId="severity-label"
          inputProps={{ id: 'severity-input' }}
          onChange={(e) =>
            onChange({
              ...value,
              severity: e.target.value,
              type: e.target.value,
            })
          }
        >
          {ISSUE_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        label="Description"
        multiline
        rows={2}
        value={value?.description ?? ''}
        onChange={(e) => onChange({ ...value, description: e.target.value })}
        size="small"
        fullWidth
      />
    </Stack>
  ),

  relation: ({
    label,
    value,
    path,
    required,
    readOnly,
    onChange,
    error,
    helperText,
  }) => (
    <RelationField
      label={label}
      value={value}
      path={path}
      multiple={false}
      required={required}
      readOnly={readOnly}
      onChange={onChange}
      error={error}
      helperText={helperText}
    />
  ),

  relations: ({
    label,
    value,
    path,
    required,
    readOnly,
    onChange,
    error,
    helperText,
  }) => (
    <RelationField
      label={label}
      value={value}
      path={path}
      multiple={true}
      required={required}
      readOnly={readOnly}
      onChange={onChange}
      error={error}
      helperText={helperText}
    />
  ),

  content: ({ label, variant }) => (
    <Typography variant={variant} sx={{ whiteSpace: 'pre-line' }}>
      {label}
    </Typography>
  ),

  default: ({
    label,
    value,
    type,
    required,
    placeholder,
    readOnly,
    onChange,
    error,
    helperText,
    minLength,
    maxLength,
    min,
    max,
  }) => {
    const { error: fieldError, helperText: fieldHelperText } =
      resolveFieldError(
        error,
        helperText,
        validateField(type, value, { minLength, maxLength, min, max }),
      )
    return (
      <TextField
        label={label}
        type={TEXT_FIELD_INPUT_TYPES[type] ?? 'text'}
        required={required}
        placeholder={placeholder}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        fullWidth
        disabled={readOnly}
        error={fieldError}
        helperText={fieldHelperText}
      />
    )
  },
}
